<script setup lang="ts">
import DrawerLoader from '@ui/components/drawer/DrawerLoader.vue'
import OldIconButton from '@ui/components/oldIcons/OldIconButton.vue'
import { ButtonVariant } from '@ui/styles'
import { isLtLg } from '@ui/utils/breakpoints'
import { useActivated } from '@ui/utils/useActivated'
import { useOverlayScrollLock } from '@ui/utils/useOverlayScrollLock'
import { useResizeObserver } from '@vueuse/core'
import Thumbtack from '~icons/cue-filled/thumbtack'
import ArrowLeftSmall from '~icons/cue-regular/arrow-left-small'
import Times from '~icons/cue-regular/times'
import { useDrawerGroup } from './drawerGroup'
import { useDrawerTransition } from './useDrawerTransition'

withDefaults(
  defineProps<{
    label?: string
    pinnable?: boolean
    width?: string
    zIndex?: number
    order?: number
    pending?: boolean
    keep?: boolean
    padding?: boolean
    stretch?: boolean
    arrow?: boolean
  }>(),
  {
    label: '',
    pinnable: false,
    width: '520px',
    zIndex: 300,
    pending: false,
    keep: false,
    padding: true,
    arrow: false,
  },
)

const emit = defineEmits<{
  'click:arrow': []
  'afterLeave': []
}>()

defineSlots<{
  'header'?: () => void
  'header:action'?: () => void
  'default'?: () => void
  'actions'?: () => void
}>()
const show = defineModel<boolean>('show', { default: false })
const pinned = defineModel<boolean>('pin', { default: false })
// Reuse the element when closing is interrupted; unmount after a completed leave.
const rendered = ref(show.value)
watch(show, visible => {
  if (visible) {
    rendered.value = true
  }
})

/** Побочный эффект от телепорта: не закрывается drawer при переходе на другую страницу */
const activated = useActivated()
watch(activated, v => {
  if (!v && show.value) {
    show.value = false
  }
})

useOverlayScrollLock(computed(() => show.value && isLtLg.value))

const instance = getCurrentInstance()

const hasArrowClickListener = computed(() => !!instance?.vnode.props?.['onClick:arrow'])

function onArrowClick() {
  if (hasArrowClickListener.value) {
    emit('click:arrow')
    return
  }

  show.value = !show.value
}

const transition = useDrawerTransition()
const drawer = ref<HTMLElement>()
const drawerWidth = ref(0)
useResizeObserver(
  drawer,
  () => {
    if (drawer.value) {
      drawerWidth.value = drawer.value.getBoundingClientRect().width
    }
  },
  { box: 'border-box' },
)
const group = useDrawerGroup()
const grouped = computed(() => !!group && !(pinned.value && !isLtLg.value))
// A missing Teleport target leaves its children unmounted, so later updates fail.
const groupTarget = computed(() => unref<string | HTMLElement | null | undefined>(group?.target))
const groupLayer = group?.register(
  computed(() => grouped.value && activated.value && show.value),
  drawerWidth,
)

function onAfterLeave() {
  groupLayer?.afterLeave()
  if (!show.value) {
    rendered.value = false
    emit('afterLeave')
  }
}

watch(grouped, value => {
  if (!value) {
    groupLayer?.afterLeave()
  }
})

watch(
  [drawer, show],
  ([element, visible]) => {
    if (element && visible) {
      // Measure after insertion, before ResizeObserver and the first animation frame.
      drawerWidth.value = element.getBoundingClientRect().width
    }
  },
  { flush: 'post' },
)
</script>

<template lang="pug">
Teleport(v-if="!grouped || groupTarget" :to="grouped ? groupTarget : ((pinned && !isLtLg) ? '#drawer' : '#drawer-inner')" :disabled="!activated" defer)
  .drawer-padding.flex-shrink-0.position-relative(
    v-if="activated"
    :class="{ 'drawer-padding--grouped': grouped }"
    :inert="!show"
    :style="{ width: grouped ? '100%' : `${show ? drawerWidth : 0}px`, order, zIndex: grouped ? groupLayer?.zIndex.value : zIndex, '--drawer-duration': `${transition.duration}s` }"
  )
    Transition(name="slide" persisted @afterLeave="onAfterLeave")
      .drawer.drawer-width.t-0.r-0(v-if="keep || rendered" v-show="show" ref="drawer" :style="{ width }" :class="(pinned && !isLtLg) ? 'position-fixed' : 'position-absolute'")
        .drawer-scroll.flex.col-0.w-100p.h-100p.overflow-y-auto.position-relative(:class="{ 'drawer-scroll--stretch': stretch }")
          .general.flex.col-0.position-relative
            .header.flex-space-between-center.row-16.position-sticky.top-0.z-index-1
              .flex-start-center.row-12.typ-22-24-600.overflow-hidden
                UiIconButton.m-8(v-if="arrow" :name="ArrowLeftSmall" :variant="ButtonVariant.Function" @click="onArrowClick")
                OldIconButton.s-40.p-8.pin(v-if="pinnable && !isLtLg" :name="Thumbtack" :class="{ pinned }" @click="pinned = !pinned")
                slot(name="header")
                  span.text-ellipsis {{ $t(label) }}
              .flex.row-6
                slot(name="header:action")
                OldIconButton.icon-close.s-40.p-8(:name="Times" @click="show = false")
            .content.flex.col-16(:class="{ padding }")
              DrawerLoader(v-if="pending")
              slot
          .actions.flex.row-16.position-sticky.bottom-0.w-100p.m-t-auto.z-index-100(v-if="$slots.actions" class="sm_p-16-30")
            slot(name="actions")
</template>

<style scoped>
.pin {
  color: var(--cue-content-neutral-primary);

  &.pinned {
    color: var(--cue-content-primary-default);
  }
}

.header {
  background: var(--cue-bg-surface-highest);
  color: var(--cue-content-neutral-primary);
  --uno: p-16-16-0;

  @screen sm {
    --uno: p-28-28-0;
  }
}

.content.padding {
  --uno: p-16;

  @screen sm {
    --uno: p-16-28;
  }
}

.icon-close {
  color: var(--cue-content-neutral-secondary);
}

.drawer-padding {
  transition: width var(--drawer-duration) ease-in-out;

  @screen lt-lg {
    position: fixed;
    right: 0;
  }
}

.drawer-padding--grouped {
  --uno: position-absolute t-0 r-0 w-100p;
  transition: none;

  @screen lt-lg {
    --uno: position-absolute;
  }

  .slide-enter-from,
  .slide-leave-to {
    --uno: opacity-0;
  }
}

.drawer {
  background: var(--cue-bg-surface-highest);
  border-left: 1px solid var(--cue-border-neutral-none);
  box-shadow: 6px 20px 36px 0 color-mix(in srgb, var(--cue-static-neutral-inverse) 10%, transparent);
  transition:
    width var(--drawer-duration) ease-in-out,
    transform var(--drawer-duration) ease-in-out,
    opacity var(--drawer-duration) ease-in-out;
  pointer-events: auto;
}

.drawer-width {
  height: 100vh;
  @supports (height: 100dvh) {
    height: 100dvh;
  }
  max-width: 100vw;
  @supports (max-width: 100dvw) {
    max-width: 100dvw;
  }

  @screen lt-sm {
    min-width: 100vw;
    @supports (min-width: 100dvw) {
      min-width: 100dvw;
    }
  }
}

.drawer-scroll {
  overscroll-behavior-y: none;
}

.general {
  min-height: max-content;
}

.drawer-scroll--stretch {
  --uno: overflow-hidden;

  .general {
    --uno: flex-1 min-h-0;
  }

  .content {
    --uno: flex-1 min-h-0;
  }
}

.actions {
  border-top: 1px solid var(--cue-border-neutral-secondary);
  background: var(--cue-bg-surface-highest);
  --uno: p-16;

  @screen sm {
    --uno: p-16-28-32;
  }
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

/* Анимация */
.slide-enter-active,
.slide-leave-active {
  transition:
    transform var(--drawer-duration) ease-in-out,
    opacity var(--drawer-duration) ease-in-out;
}
</style>
